"use client";

import { useEffect, useState } from "react";

declare global {
  interface Window {
    Tawk_API?: {
      maximize?: () => void;
      hideWidget?: () => void;
      showWidget?: () => void;
      onLoad?: () => void;
    };
  }
}

/**
 * Small custom chat button that opens the Tawk.to chat panel on click.
 * Replaces the default oversized Tawk.to widget bubble.
 *
 * Pairs with the Tawk.to script in `app/layout.tsx`, which hides the
 * default widget on load via `Tawk_API.onLoad → hideWidget()`.
 */
export default function CustomChatButton() {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // Poll for Tawk_API readiness (it loads lazily, after page idle)
    let cancelled = false;
    const interval = window.setInterval(() => {
      if (cancelled) return;
      if (window.Tawk_API?.maximize) {
        setReady(true);
        window.clearInterval(interval);
      }
    }, 500);

    // Stop polling after 30s to avoid leaks if the script never loads
    const timeout = window.setTimeout(() => {
      cancelled = true;
      window.clearInterval(interval);
    }, 30_000);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      window.clearTimeout(timeout);
    };
  }, []);

  const handleClick = () => {
    window.Tawk_API?.maximize?.();
  };

  if (!ready) return null;

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label="Open chat"
      data-chat-button=""
      className="btn-glass-primary fixed bottom-4 right-4 z-40 h-12 w-12 sm:bottom-6 sm:right-6"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        className="h-6 w-6"
        aria-hidden="true"
      >
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </svg>
    </button>
  );
}
